import {
  Creator, Approval, Partnership, Contract, ActivityEvent, Agent,
  Followup, AppNotification, TimelineEntry, Automation, MemoryEntry,
} from '@/types/entities';

/**
 * DEMO DATA SERVICE — the single local persistence layer for PREVIEW mode.
 * Per the brief: "Do NOT scatter localStorage calls throughout UI
 * components... create/extend a proper preview/demo repository/service
 * layer." Every localStorage read/write in the whole app lives HERE and
 * nowhere else. `dataService.ts` is the only thing that imports this file;
 * UI components import dataService, never this one directly.
 *
 * SCHEMA_VERSION exists so a future release can migrate or discard an old
 * shape instead of crashing on a stale localStorage blob.
 */
const STORAGE_KEY = 'ugarits_demo_data_v1';
// v2 = Phase C's DemoSettings expansion. v3 = Phase D foundation's agent
// roster (10 department agents). v4 = Phase D completion's new
// `automations`/`memoryEntries` arrays (Automations + Shared Memory
// modules). v5 = Phase D completion's Settings integration — new
// `automationRules`/`supportRules` DemoSettings fields (Automations
// default approval level, Support diagnostics-run reminder) — old
// localStorage blobs predate all of these; per this file's "no migration
// path yet" rule that's an honest reset to seed() rather than
// `undefined.automations`/`undefined.automationRules`.
const SCHEMA_VERSION = 5;

export interface DemoSettings {
  // GENERAL
  companyName: string;
  legalName: string;
  defaultLandingRoute: string;
  timezone: string;
  defaultCurrency: string;
  // BRANDING — real values persisted through this same service; consumed
  // by Export/Print branding today, by real logo rendering once a real
  // asset upload exists (there is no fake logo file here, only a state flag).
  branding: { appName: string; brandColor: string; hasLogo: boolean };
  // APPEARANCE
  theme: 'light' | 'dark';
  density: 'comfortable' | 'compact';
  sidebarCollapsed: boolean;
  reducedMotion: boolean;
  // LANGUAGE
  dateFormat: 'YYYY-MM-DD' | 'DD/MM/YYYY' | 'MM/DD/YYYY';
  numberFormat: 'western' | 'arabic-indic';
  // NOTIFICATIONS
  notificationPrefs: { followupDue: boolean; approvalWaiting: boolean; creatorAtRisk: boolean; contractExpiring: boolean; systemAlerts: boolean };
  // CREATORS
  creatorRules: { defaultFollowupDays: number; riskThresholdDays: number; defaultOwner: string; idPrefix: string };
  // FOLLOW-UPS
  followupRules: { defaultPriority: 'low' | 'medium' | 'high'; overdueBehavior: 'flag' | 'escalate'; reminderEnabled: boolean };
  // APPROVALS
  approvalRules: { defaultPermissionLevel: 1 | 2 | 3; expirationDays: number; highRiskConfirm: boolean };
  // PARTNERSHIPS
  partnershipRules: { idPrefix: string; defaultCurrency: string; renewalWarningDays: number };
  // CONTRACTS
  contractRules: { idPrefix: string; expiryWarningDays: number; renewalWarningDays: number; defaultNoticePeriodDays: number };
  // AGENTS — defaults applied when creating a new agent; real permission
  // enforcement still requires the live backend (see Approvals engine).
  agentRules: { defaultPermissionLevel: 1 | 2 | 3 };
  // AUTOMATIONS — default approval level applied when creating a new
  // automation (AutomationsPage passes this into AutomationFormDrawer).
  automationRules: { defaultApprovalLevel: 1 | 2 | 3 };
  // SUPPORT — a real local preference, persisted like every other
  // setting; SupportPage reads/honors it, it isn't a fake toggle.
  supportRules: { diagnosticsReminderEnabled: boolean };
  // EXPORT
  exportSettings: { defaultFileType: 'csv' | 'pdf'; includeBranding: boolean; includeTimestamp: boolean; fileNamePrefix: string };
}

interface DemoState {
  schemaVersion: number;
  creators: Creator[];
  approvals: Approval[];
  partnerships: Partnership[];
  contracts: Contract[];
  agents: Agent[];
  activity: ActivityEvent[];
  followups: Followup[];
  automations: Automation[];
  memoryEntries: MemoryEntry[];
  notificationsReadIds: string[];
  clearedNotificationIds: string[];
  settings: DemoSettings;
  counters: Record<string, number>;
  connectorOverrides: Record<string, { label?: string; enabledUi?: boolean; notes?: string }>;
}

export interface ConnectorOverride { label?: string; enabledUi?: boolean; notes?: string }

// ---------------------------------------------------------------------
// SEED DATA — the original PREVIEW dataset. Reset Demo Data restores
// exactly this (a fresh deep clone), never a mutated copy.
// ---------------------------------------------------------------------
function seed(): DemoState {
  const now = '2026-08-19T09:00:00.000Z'; // fixed seed timestamp — real timestamps come from nowIso() at interaction time, never Date.now() at module-load time, so re-seeding is reproducible
  return {
    schemaVersion: SCHEMA_VERSION,
    creators: [
      { id: 'UGC-0027', fullName: 'Fatima Rashed', tiktokUsername: '@fatima.live', phone: '+971 55 000 1234', whatsapp: '+971 55 000 1234', email: 'fatima@example.com', country: 'UAE', emirate: 'Dubai', language: 'Arabic', source: 'Referral', joinDate: '2026-02-18', status: 'active', ownerId: 'sara', tiktokAgencyStatus: 'linked', onboardingStatus: 'complete', documentsStatus: 'complete', lastContact: '2026-08-17', nextFollowup: '2026-08-19', nextAction: 'Send follow-up message', followers: 184000, liveHours: 62, lastLive: '2026-08-18', riskLevel: 'low', notes: 'Strong consistent performer.', timeline: [ { id: 't1', label: 'Joined Ugarits', createdAt: '2026-02-18T10:00:00.000Z', actor: 'System' }, { id: 't2', label: 'TikTok agency linked', createdAt: '2026-02-20T10:00:00.000Z', actor: 'Sara' } ] },
      { id: 'UGC-0018', fullName: 'Yousef Khalil', tiktokUsername: '@yousefk', phone: '+971 50 000 4521', email: 'yousef@example.com', country: 'UAE', emirate: 'Sharjah', language: 'Arabic', source: 'Outbound', joinDate: '2026-08-04', status: 'interested', ownerId: 'sara', tiktokAgencyStatus: 'pending', onboardingStatus: 'in_progress', documentsStatus: 'pending', lastContact: '2026-08-16', nextFollowup: '2026-08-19', nextAction: 'Phone follow-up', followers: 22000, liveHours: 4, riskLevel: 'medium', timeline: [ { id: 't3', label: 'First contact', createdAt: '2026-08-04T10:00:00.000Z', actor: 'Sara' } ] },
      { id: 'UGC-0009', fullName: 'Layla Mansour', tiktokUsername: '@laylam', phone: '+971 52 000 8890', email: 'layla@example.com', country: 'UAE', emirate: 'Abu Dhabi', language: 'Arabic', source: 'Referral', joinDate: '2026-04-20', status: 'at_risk', ownerId: 'omar', tiktokAgencyStatus: 'linked', onboardingStatus: 'complete', documentsStatus: 'complete', lastContact: '2026-08-05', nextFollowup: '2026-08-19', nextAction: 'Urgent retention call', followers: 95000, liveHours: 8, lastLive: '2026-08-05', riskLevel: 'high', notes: 'LIVE hours dropped sharply this month.', timeline: [ { id: 't4', label: 'Flagged at risk', note: 'LIVE hours down 70% month-over-month', createdAt: '2026-08-10T10:00:00.000Z', actor: 'System' } ] },
    ],
    approvals: [
      { id: 'appr-1', agentId: 'creator_manager', targetEntityType: 'creator', targetEntityId: 'UGC-0034', actionTitle: 'Send WhatsApp onboarding message', reason: 'New creator joined today', payload: 'Hi Ahmad, welcome to Ugarits...', permissionLevel: 2, status: 'pending', createdAt: '2026-08-19T08:54:00.000Z' },
      { id: 'appr-2', agentId: 'sales', targetEntityType: 'partnership', targetEntityId: 'UG-PART-0002', actionTitle: 'Send partnership follow-up email', reason: 'No response in 9 days', payload: 'Following up on our proposal...', permissionLevel: 2, status: 'pending', createdAt: '2026-08-19T07:10:00.000Z' },
    ],
    partnerships: [
      { id: 'UG-PART-0001', partnerName: 'Nova Talent Group', legalName: 'Nova Talent Group FZ-LLC', type: 'strategic', country: 'UAE', emirate: 'Dubai', industry: 'Talent Management', status: 'active', startDate: '2025-09-01', endDate: '2026-08-31', renewalDate: '2026-08-01', autoRenewal: false, ownerId: 'maher', primaryContractId: 'UG-CON-0001', website: 'https://novatalent.example.com', notes: 'Primary strategic partner in Dubai.', contacts: [ { id: 'c1', role: 'primary', name: 'Adam Farouk', jobTitle: 'Partnerships Lead', phone: '+971 4 000 1111', email: 'adam@novatalent.example.com', website: 'https://novatalent.example.com' } ], timeline: [ { id: 'pt1', label: 'Partnership activated', createdAt: '2025-09-01T10:00:00.000Z', actor: 'Maher' } ], ledger: [] },
      { id: 'UG-PART-0002', partnerName: 'Gulf Creators Collective', type: 'creator_partner', country: 'UAE', status: 'pending', ownerId: 'sara', contacts: [], timeline: [], ledger: [] },
    ],
    contracts: [
      { id: 'UG-CON-0001', partnershipId: 'UG-PART-0001', title: 'Master Services & Revenue Share Agreement', contractType: 'Master Services', version: 'v1', status: 'active', effectiveDate: '2025-09-01', expiryDate: '2026-08-31', renewalDate: '2026-08-01', noticePeriodDays: 30, autoRenewal: false, signedByUgarits: true, signedByPartner: true, signatureDate: '2025-08-28', currency: 'AED', contractValue: 240000, revenueShareApplicable: true, internalOwner: 'maher', hasSignedFile: false },
      { id: 'UG-CON-0002', partnershipId: 'UG-PART-0002', title: 'Creator Partnership Agreement', contractType: 'Creator Partnership', version: 'v1', status: 'draft', signedByUgarits: false, signedByPartner: false, hasSignedFile: false },
    ],
    agents: [
      { id: 'executive', name: 'Ugarits Executive Agent', department: 'executive', role: 'Executive summary & prioritization', provider: 'anthropic', model: 'claude-sonnet', permissionLevel: 1, status: 'waiting_credentials', description: 'Coordinates specialists, reads authorized high-level state, routes tasks, summarizes results, escalates approvals. Not a legal decision authority — the human Managing Director remains that.', allowedActions: 'Read, draft, summarize', forbiddenActions: 'Send, delete, spend' },
      { id: 'operations', name: 'Operations Agent', department: 'operations', role: 'Inbox triage & workflow monitoring', provider: 'anthropic', model: 'claude-sonnet', permissionLevel: 1, status: 'waiting_credentials', description: 'Triages inbound items, routes tasks, prepares a daily operations brief.', allowedActions: 'Read, draft, flag', forbiddenActions: 'Send, delete' },
      { id: 'creator_manager', name: 'Creator Manager Agent', department: 'creator_manager', role: 'Creator follow-ups & retention', provider: 'anthropic', model: 'claude-sonnet', permissionLevel: 2, status: 'waiting_credentials', description: 'Drafts follow-up messages and flags at-risk creators.', allowedActions: 'Draft messages, flag risk', forbiddenActions: 'Send without approval' },
      { id: 'content_director', name: 'Content Director Agent', department: 'content', role: 'Content pipeline', provider: 'openai', model: 'gpt-4.1', permissionLevel: 2, status: 'waiting_credentials', description: 'Research, ideas, briefs, scripts, and content pipeline/performance review.', allowedActions: 'Research, draft briefs', forbiddenActions: 'Publish, send' },
      { id: 'sales', name: 'Sales Agent', department: 'sales', role: 'Partnership & lead outreach', provider: 'openai', model: 'gpt-4.1', permissionLevel: 2, status: 'waiting_credentials', description: 'Lead qualification, pipeline review, follow-up and proposal preparation.', allowedActions: 'Draft, prepare proposals', forbiddenActions: 'Autonomous external outreach' },
      { id: 'growth', name: 'Growth Agent', department: 'growth', role: 'Marketing & growth research', provider: 'anthropic', model: 'claude-sonnet', permissionLevel: 1, status: 'waiting_credentials', description: 'Competitor research, campaign analysis, SEO research, growth recommendations.', allowedActions: 'Research, recommend', forbiddenActions: 'Change ad spend, publish' },
      { id: 'finance', name: 'Finance Agent', department: 'finance', role: 'Financial monitoring', provider: 'anthropic', model: 'claude-sonnet', permissionLevel: 1, status: 'waiting_credentials', description: 'Invoice monitoring, receivables, expense categorization, financial summaries. Never autonomous payment execution. (Conceptual permission model: Level 0 — Observe; the existing Agent.permissionLevel field only spans 1–3, see AI Operations for the finer-grained concept.)', allowedActions: 'Read, summarize', forbiddenActions: 'Execute payments, modify ledger' },
      { id: 'social_analytics', name: 'Analytics Agent', department: 'analytics', role: 'Cross-channel performance analysis', provider: 'anthropic', model: 'claude-sonnet', permissionLevel: 1, status: 'waiting_credentials', description: 'Cross-channel reporting, creator analytics, business insights — never fabricated without real data.', allowedActions: 'Read, analyze, report', forbiddenActions: 'Send, publish' },
      { id: 'developer', name: 'Developer Agent', department: 'developer', role: 'System/automation maintenance', provider: 'anthropic', model: 'claude-sonnet', permissionLevel: 2, status: 'waiting_credentials', description: 'Diagnostics, code/build analysis, deployment recommendations, error investigation. No unrestricted production shell access by default.', allowedActions: 'Diagnose, recommend', forbiddenActions: 'Production shell access, deploy' },
      { id: 'support', name: 'Support Agent', department: 'support', role: 'Diagnostics & recovery guidance', provider: 'anthropic', model: 'claude-sonnet', permissionLevel: 1, status: 'waiting_credentials', description: 'System diagnostics, error interpretation, recovery guidance, escalation preparation. Reads only sanitized diagnostics.', allowedActions: 'Read sanitized diagnostics, advise', forbiddenActions: 'Read secrets/credentials' },
    ],
    activity: [
      { id: 'a1', actorType: 'agent', actorLabel: 'AI', action: 'Drafted a message', targetEntityType: 'creator', targetEntityId: 'UGC-0034', result: 'info', createdAt: '2026-08-19T08:55:00.000Z' },
    ],
    followups: [
      { id: 'FU-0001', entityType: 'creator', entityId: 'UGC-0027', entityLabel: 'Fatima Rashed', title: 'Send follow-up message', dueDate: '2026-08-19', priority: 'medium', ownerId: 'sara', status: 'open', createdAt: '2026-08-15T10:00:00.000Z' },
      { id: 'FU-0002', entityType: 'creator', entityId: 'UGC-0018', entityLabel: 'Yousef Khalil', title: 'Phone follow-up', dueDate: '2026-08-19', priority: 'medium', ownerId: 'sara', status: 'open', createdAt: '2026-08-16T10:00:00.000Z' },
      { id: 'FU-0003', entityType: 'creator', entityId: 'UGC-0009', entityLabel: 'Layla Mansour', title: 'Urgent retention call', dueDate: '2026-08-18', priority: 'high', ownerId: 'omar', status: 'open', createdAt: '2026-08-10T10:00:00.000Z' },
    ],
    automations: [
      { id: 'UG-AUTO-0001', name: 'Creator Follow-up Reminder', description: 'Notify the owner when a creator follow-up becomes overdue.', trigger: 'Follow-up due date passes with status still open', conditions: 'Follow-up.status = open AND dueDate < today', actions: 'Create local notification for the follow-up owner', ownerAgent: 'creator_manager', approvalLevel: 1, status: 'waiting_connector' },
      { id: 'UG-AUTO-0002', name: 'Contract Expiry Watch', description: 'Flag contracts entering the renewal warning window.', trigger: 'Daily local check against Settings → Contracts expiry warning window', conditions: 'Contract.expiryDate within window AND status != archived', actions: 'Create local notification; add to Overview \"Needs Attention\"', ownerAgent: 'finance', approvalLevel: 1, status: 'waiting_connector' },
    ],
    memoryEntries: [
      { id: 'UG-MEM-0001', title: 'Ugarits brand voice — Arabic/English', domain: 'brand', content: 'Ugarits communicates as a premium, direct, execution-focused agency. Arabic copy uses Gulf-friendly conversational tone; English copy stays concise and confident. Avoid generic marketing filler.', source: 'Founder guidance', tags: ['brand', 'voice', 'copy'], owner: 'maher', visibility: 'team', agentAccess: ['content', 'growth', 'executive'], createdAt: '2026-08-01T09:00:00.000Z', updatedAt: '2026-08-01T09:00:00.000Z' },
      { id: 'UG-MEM-0002', title: 'Creator follow-up SOP', domain: 'sop', content: 'Follow up with a new creator within 3 days of joining. Escalate to a phone call if two messages go unanswered. Flag as at-risk if LIVE hours drop more than 50% month-over-month.', source: 'Creator Manager playbook', tags: ['creators', 'sop', 'retention'], owner: 'sara', visibility: 'team', agentAccess: ['creator_manager', 'executive', 'analytics'], createdAt: '2026-08-05T09:00:00.000Z', updatedAt: '2026-08-05T09:00:00.000Z' },
    ],
    notificationsReadIds: [],
    clearedNotificationIds: [],
    settings: {
      companyName: 'Ugarits Marketing LLC',
      legalName: 'Ugarits Marketing LLC',
      defaultLandingRoute: '/',
      timezone: 'Asia/Dubai',
      defaultCurrency: 'AED',
      branding: { appName: 'Ugarits Command', brandColor: '#c9932a', hasLogo: false },
      theme: 'light',
      density: 'comfortable',
      sidebarCollapsed: false,
      reducedMotion: false,
      dateFormat: 'YYYY-MM-DD',
      numberFormat: 'western',
      notificationPrefs: { followupDue: true, approvalWaiting: true, creatorAtRisk: true, contractExpiring: true, systemAlerts: true },
      creatorRules: { defaultFollowupDays: 3, riskThresholdDays: 14, defaultOwner: 'unassigned', idPrefix: 'UGC' },
      followupRules: { defaultPriority: 'medium', overdueBehavior: 'flag', reminderEnabled: true },
      approvalRules: { defaultPermissionLevel: 2, expirationDays: 7, highRiskConfirm: true },
      partnershipRules: { idPrefix: 'UG-PART', defaultCurrency: 'AED', renewalWarningDays: 30 },
      contractRules: { idPrefix: 'UG-CON', expiryWarningDays: 30, renewalWarningDays: 30, defaultNoticePeriodDays: 30 },
      agentRules: { defaultPermissionLevel: 2 },
      automationRules: { defaultApprovalLevel: 2 },
      supportRules: { diagnosticsReminderEnabled: false },
      exportSettings: { defaultFileType: 'csv', includeBranding: true, includeTimestamp: true, fileNamePrefix: 'ugarits' },
    },
    counters: { creator: 34, partnership: 2, contract: 2, approval: 2, agent: 10, followup: 3, automation: 2, memory: 2 },
    connectorOverrides: {},
  };
  // NB: `now` above is intentionally unused at runtime beyond documenting
  // the seed's reference date — every timeline/activity entry has its own
  // literal timestamp so the seed is reproducible.
  void now;
}

let state: DemoState = load();

function load(): DemoState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return seed();
    const parsed = JSON.parse(raw) as DemoState;
    if (parsed.schemaVersion !== SCHEMA_VERSION) return seed(); // no migration path yet — honest reset rather than a corrupt shape
    return parsed;
  } catch {
    return seed();
  }
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // storage unavailable (private mode / quota) — the session still works, just doesn't survive refresh
  }
}

function nowIso(): string {
  return new Date().toISOString();
}

function nextId(kind: keyof DemoState['counters'], prefix: string, pad = 4): string {
  state.counters[kind] = (state.counters[kind] ?? 0) + 1;
  return `${prefix}-${String(state.counters[kind]).padStart(pad, '0')}`;
}

function pushActivity(entry: Omit<ActivityEvent, 'id' | 'createdAt'>) {
  state.activity.unshift({ ...entry, id: `act-${Math.floor(Math.random() * 1e9)}`, createdAt: nowIso() });
  if (state.activity.length > 300) state.activity.length = 300;
}

// Local PREVIEW actions are attributed to a human, never faked as "AI" or
// "System" — those actor labels are reserved for events a real agent or
// automation actually produced.
const LOCAL_ACTOR = 'Maher (local)';

export const demoDataService = {
  // ---- Creators --------------------------------------------------
  listCreators: () => state.creators.filter((c) => !c.archived),
  listAllCreators: () => state.creators,
  getCreator: (id: string) => state.creators.find((c) => c.id === id),
  createCreator: (input: Partial<Creator> & { fullName: string; tiktokUsername: string }): Creator => {
    const creator: Creator = {
      id: nextId('creator', state.settings.creatorRules.idPrefix),
      fullName: input.fullName,
      tiktokUsername: input.tiktokUsername,
      phone: input.phone ?? '',
      whatsapp: input.whatsapp,
      email: input.email ?? '',
      country: input.country ?? '',
      emirate: input.emirate ?? '',
      language: input.language ?? '',
      source: input.source,
      joinDate: nowIso().slice(0, 10),
      status: input.status ?? 'new',
      ownerId: input.ownerId ?? state.settings.creatorRules.defaultOwner,
      riskLevel: input.riskLevel ?? 'low',
      notes: input.notes,
      timeline: [{ id: `t-${Date.now()}`, label: 'Creator added (PREVIEW)', createdAt: nowIso(), actor: LOCAL_ACTOR }],
    };
    state.creators.unshift(creator);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: 'Creator added', targetEntityType: 'creator', targetEntityId: creator.id, result: 'good' });
    persist();
    return creator;
  },
  updateCreator: (id: string, patch: Partial<Creator>): Creator | undefined => {
    const c = state.creators.find((x) => x.id === id);
    if (!c) return undefined;
    Object.assign(c, patch);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: 'Creator updated', targetEntityType: 'creator', targetEntityId: id, result: 'info' });
    persist();
    return c;
  },
  addCreatorTimelineEntry: (id: string, label: string, note?: string): TimelineEntry | undefined => {
    const c = state.creators.find((x) => x.id === id);
    if (!c) return undefined;
    const entry: TimelineEntry = { id: `t-${Date.now()}`, label, note, createdAt: nowIso(), actor: LOCAL_ACTOR };
    c.timeline.unshift(entry);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: label, targetEntityType: 'creator', targetEntityId: id, result: 'info' });
    persist();
    return entry;
  },
  archiveCreator: (id: string): void => {
    const c = state.creators.find((x) => x.id === id);
    if (!c) return;
    c.archived = true;
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: 'Creator archived', targetEntityType: 'creator', targetEntityId: id, result: 'warn' });
    persist();
  },
  deleteCreator: (id: string): void => {
    state.creators = state.creators.filter((c) => c.id !== id);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: 'Demo creator deleted', targetEntityType: 'creator', targetEntityId: id, result: 'crit' });
    persist();
  },

  // ---- Followups ---------------------------------------------------
  listFollowups: () => state.followups,
  createFollowup: (input: Omit<Followup, 'id' | 'createdAt' | 'status'>): Followup => {
    const fu: Followup = { ...input, id: nextId('followup', 'FU'), status: 'open', createdAt: nowIso() };
    state.followups.unshift(fu);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: 'Follow-up created', targetEntityType: fu.entityType, targetEntityId: fu.entityId, result: 'info' });
    persist();
    return fu;
  },
  updateFollowup: (id: string, patch: Partial<Followup>) => {
    const fu = state.followups.find((f) => f.id === id);
    if (!fu) return undefined;
    Object.assign(fu, patch);
    persist();
    return fu;
  },
  completeFollowup: (id: string) => {
    const fu = state.followups.find((f) => f.id === id);
    if (!fu) return;
    fu.status = 'done';
    fu.completedAt = nowIso();
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: 'Follow-up completed', targetEntityType: fu.entityType, targetEntityId: fu.entityId, result: 'good' });
    persist();
  },
  reopenFollowup: (id: string) => {
    const fu = state.followups.find((f) => f.id === id);
    if (!fu) return;
    fu.status = 'open';
    fu.completedAt = undefined;
    persist();
  },
  deleteFollowup: (id: string) => {
    state.followups = state.followups.filter((f) => f.id !== id);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: 'Demo follow-up deleted', result: 'crit' });
    persist();
  },

  // ---- Approvals ---------------------------------------------------
  listApprovals: () => state.approvals,
  decideApproval: (id: string, decision: 'approved' | 'rejected') => {
    const a = state.approvals.find((x) => x.id === id);
    if (!a) return;
    a.status = decision;
    a.decidedAt = nowIso();
    a.decidedBy = LOCAL_ACTOR;
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: decision === 'approved' ? 'Approval approved' : 'Approval rejected', targetEntityType: a.targetEntityType, targetEntityId: a.targetEntityId, result: decision === 'approved' ? 'good' : 'crit' });
    persist();
  },
  archiveApproval: (id: string) => {
    const a = state.approvals.find((x) => x.id === id);
    if (!a) return;
    a.status = 'archived';
    persist();
  },
  deleteApproval: (id: string) => {
    state.approvals = state.approvals.filter((a) => a.id !== id);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: 'Demo approval deleted', result: 'crit' });
    persist();
  },

  // ---- Partnerships --------------------------------------------------
  listPartnerships: () => state.partnerships.filter((p) => !p.archived),
  getPartnership: (id: string) => state.partnerships.find((p) => p.id === id),
  createPartnership: (input: Partial<Partnership> & { partnerName: string }): Partnership => {
    const p: Partnership = {
      id: nextId('partnership', state.settings.partnershipRules.idPrefix),
      partnerName: input.partnerName,
      legalName: input.legalName,
      type: input.type ?? 'other',
      country: input.country ?? '',
      emirate: input.emirate,
      industry: input.industry,
      status: input.status ?? 'pending',
      startDate: input.startDate,
      endDate: input.endDate,
      ownerId: input.ownerId ?? state.settings.creatorRules.defaultOwner,
      website: input.website,
      notes: input.notes,
      contacts: [],
      timeline: [{ id: `pt-${Date.now()}`, label: 'Partnership created (PREVIEW)', createdAt: nowIso(), actor: LOCAL_ACTOR }],
      ledger: [],
    };
    state.partnerships.unshift(p);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: 'Partnership created', targetEntityType: 'partnership', targetEntityId: p.id, result: 'good' });
    persist();
    return p;
  },
  updatePartnership: (id: string, patch: Partial<Partnership>) => {
    const p = state.partnerships.find((x) => x.id === id);
    if (!p) return undefined;
    Object.assign(p, patch);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: 'Partnership updated', targetEntityType: 'partnership', targetEntityId: id, result: 'info' });
    persist();
    return p;
  },
  addPartnershipContact: (id: string, contact: Omit<Partnership['contacts'][number], 'id'>) => {
    const p = state.partnerships.find((x) => x.id === id);
    if (!p) return undefined;
    const c = { ...contact, id: `pc-${Date.now()}` };
    p.contacts.push(c);
    persist();
    return c;
  },
  addPartnershipTimelineEntry: (id: string, label: string, note?: string) => {
    const p = state.partnerships.find((x) => x.id === id);
    if (!p) return undefined;
    const entry: TimelineEntry = { id: `pt-${Date.now()}`, label, note, createdAt: nowIso(), actor: LOCAL_ACTOR };
    p.timeline.unshift(entry);
    persist();
    return entry;
  },
  addPartnershipLedgerEntry: (id: string, entry: Omit<Partnership['ledger'][number], 'id' | 'createdAt'>) => {
    const p = state.partnerships.find((x) => x.id === id);
    if (!p) return undefined;
    const row = { ...entry, id: `led-${Date.now()}`, createdAt: nowIso() };
    p.ledger.unshift(row);
    persist();
    return row;
  },
  archivePartnership: (id: string) => {
    const p = state.partnerships.find((x) => x.id === id);
    if (!p) return;
    p.archived = true;
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: 'Partnership archived', targetEntityType: 'partnership', targetEntityId: id, result: 'warn' });
    persist();
  },
  deletePartnership: (id: string) => {
    state.partnerships = state.partnerships.filter((p) => p.id !== id);
    persist();
  },

  // ---- Contracts --------------------------------------------------
  listContracts: () => state.contracts.filter((c) => !c.archived),
  getContract: (id: string) => state.contracts.find((c) => c.id === id),
  createContract: (input: Partial<Contract> & { partnershipId: string; title: string }): Contract => {
    const c: Contract = {
      id: nextId('contract', state.settings.contractRules.idPrefix),
      partnershipId: input.partnershipId,
      title: input.title,
      contractType: input.contractType,
      version: input.version ?? 'v1',
      status: input.status ?? 'draft',
      effectiveDate: input.effectiveDate,
      expiryDate: input.expiryDate,
      renewalDate: input.renewalDate,
      noticePeriodDays: input.noticePeriodDays,
      autoRenewal: input.autoRenewal ?? false,
      signedByUgarits: input.signedByUgarits ?? false,
      signedByPartner: input.signedByPartner ?? false,
      currency: input.currency ?? 'AED',
      contractValue: input.contractValue,
      revenueShareApplicable: input.revenueShareApplicable ?? false,
      internalOwner: input.internalOwner,
      notes: input.notes,
      hasSignedFile: false,
    };
    state.contracts.unshift(c);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: 'Contract created', targetEntityType: 'partnership', targetEntityId: c.partnershipId, result: 'good' });
    persist();
    return c;
  },
  updateContract: (id: string, patch: Partial<Contract>) => {
    const c = state.contracts.find((x) => x.id === id);
    if (!c) return undefined;
    Object.assign(c, patch);
    persist();
    return c;
  },
  duplicateContractVersion: (id: string): Contract | undefined => {
    const src = state.contracts.find((x) => x.id === id);
    if (!src) return undefined;
    const versionNum = parseInt(src.version.replace(/\D/g, ''), 10) || 1;
    const copy: Contract = { ...src, id: nextId('contract', 'UG-CON'), version: `v${versionNum + 1}`, status: 'draft', signedByUgarits: false, signedByPartner: false, hasSignedFile: false, signedFilePreviewName: undefined };
    state.contracts.unshift(copy);
    persist();
    return copy;
  },
  archiveContract: (id: string) => {
    const c = state.contracts.find((x) => x.id === id);
    if (!c) return;
    c.archived = true;
    persist();
  },
  deleteContract: (id: string) => {
    state.contracts = state.contracts.filter((c) => c.id !== id);
    persist();
  },

  // ---- AI Agents --------------------------------------------------
  listAgents: () => state.agents.filter((a) => !a.archived),
  createAgent: (input: Partial<Agent> & { name: string; provider: string; model: string }): Agent => {
    const a: Agent = {
      id: nextId('agent', 'UG-AGT').toLowerCase(),
      name: input.name,
      role: input.role,
      department: input.department ?? 'operations',
      provider: input.provider,
      model: input.model,
      permissionLevel: input.permissionLevel ?? 1,
      status: 'configured_preview',
      description: input.description,
      allowedActions: input.allowedActions,
      forbiddenActions: input.forbiddenActions,
      isPreviewCreated: true,
    };
    state.agents.push(a);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: 'AI agent added (preview)', result: 'good' });
    persist();
    return a;
  },
  updateAgent: (id: string, patch: Partial<Agent>) => {
    const a = state.agents.find((x) => x.id === id);
    if (!a) return undefined;
    Object.assign(a, patch);
    persist();
    return a;
  },
  toggleAgentPause: (id: string) => {
    const a = state.agents.find((x) => x.id === id);
    if (!a) return;
    a.status = a.status === 'paused' ? (a.isPreviewCreated ? 'configured_preview' : 'waiting_credentials') : 'paused';
    persist();
  },
  archiveAgent: (id: string) => {
    const a = state.agents.find((x) => x.id === id);
    if (!a) return;
    a.archived = true;
    persist();
  },
  deleteAgent: (id: string) => {
    state.agents = state.agents.filter((a) => a.id !== id);
    persist();
  },

  // ---- Automations --------------------------------------------------
  // Local Preview definitions only — no execution backend exists, so a
  // newly created automation always starts 'draft'/'waiting_connector',
  // never 'active' (see createAutomation below and its own comment).
  listAutomations: () => state.automations.filter((a) => !a.archived),
  getAutomation: (id: string) => state.automations.find((a) => a.id === id),
  createAutomation: (input: Partial<Automation> & { name: string; trigger: string; actions: string }): Automation => {
    const a: Automation = {
      id: nextId('automation', 'UG-AUTO'),
      name: input.name,
      description: input.description,
      trigger: input.trigger,
      conditions: input.conditions,
      actions: input.actions,
      ownerAgent: input.ownerAgent,
      approvalLevel: input.approvalLevel ?? 2,
      // Never 'active' on create — no real trigger/scheduler exists in
      // Preview. 'waiting_connector' if the trigger text mentions an
      // external source, otherwise 'draft'.
      status: /connector|webhook|api|email|whatsapp|zapier|n8n/i.test(input.trigger) ? 'waiting_connector' : 'draft',
      isPreviewCreated: true,
    };
    state.automations.push(a);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: `Automation created (preview) — ${a.name}`, result: 'good' });
    persist();
    return a;
  },
  updateAutomation: (id: string, patch: Partial<Automation>) => {
    const a = state.automations.find((x) => x.id === id);
    if (!a) return undefined;
    Object.assign(a, patch);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: `Automation edited (preview) — ${a.name}`, result: 'info' });
    persist();
    return a;
  },
  duplicateAutomation: (id: string): Automation | undefined => {
    const src = state.automations.find((a) => a.id === id);
    if (!src) return undefined;
    const copy: Automation = { ...src, id: nextId('automation', 'UG-AUTO'), name: `${src.name} (copy)`, status: 'draft', isPreviewCreated: true, lastRun: undefined, nextRun: undefined };
    state.automations.push(copy);
    persist();
    return copy;
  },
  toggleAutomationPause: (id: string) => {
    const a = state.automations.find((x) => x.id === id);
    if (!a) return;
    a.status = a.status === 'paused' ? 'draft' : 'paused';
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: `Automation ${a.status === 'paused' ? 'paused' : 'resumed'} (preview) — ${a.name}`, result: 'warn' });
    persist();
  },
  archiveAutomation: (id: string) => {
    const a = state.automations.find((x) => x.id === id);
    if (!a) return;
    a.archived = true;
    a.status = 'archived';
    persist();
  },
  deleteAutomation: (id: string) => {
    state.automations = state.automations.filter((a) => a.id !== id);
    persist();
  },

  // ---- Shared Memory ("Ugarits Brain") -------------------------------
  // Plain substring search only — no embeddings/vector store exist; see
  // types/orchestrator.ts's MemoryDomain comment for the future shape.
  listMemory: () => state.memoryEntries.filter((m) => !m.archived),
  searchMemory: (query: string, domain?: string) => {
    const q = query.trim().toLowerCase();
    return state.memoryEntries.filter((m) => {
      if (m.archived) return false;
      if (domain && domain !== 'all' && m.domain !== domain) return false;
      if (!q) return true;
      return m.title.toLowerCase().includes(q) || m.content.toLowerCase().includes(q) || m.tags.some((t) => t.toLowerCase().includes(q));
    });
  },
  getMemory: (id: string) => state.memoryEntries.find((m) => m.id === id),
  createMemory: (input: Partial<MemoryEntry> & { title: string; domain: string; content: string }): MemoryEntry => {
    const now = nowIso();
    const m: MemoryEntry = {
      id: nextId('memory', 'UG-MEM'),
      title: input.title,
      domain: input.domain,
      content: input.content,
      source: input.source,
      tags: input.tags ?? [],
      owner: input.owner ?? LOCAL_ACTOR,
      visibility: input.visibility ?? 'team',
      agentAccess: input.agentAccess ?? [],
      createdAt: now,
      updatedAt: now,
      isPreviewCreated: true,
    };
    state.memoryEntries.push(m);
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: `Memory entry created (preview) — ${m.title}`, result: 'good' });
    persist();
    return m;
  },
  updateMemory: (id: string, patch: Partial<MemoryEntry>) => {
    const m = state.memoryEntries.find((x) => x.id === id);
    if (!m) return undefined;
    Object.assign(m, patch, { updatedAt: nowIso() });
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: `Memory entry edited (preview) — ${m.title}`, result: 'info' });
    persist();
    return m;
  },
  archiveMemory: (id: string) => {
    const m = state.memoryEntries.find((x) => x.id === id);
    if (!m) return;
    m.archived = true;
    persist();
  },
  deleteMemory: (id: string) => {
    state.memoryEntries = state.memoryEntries.filter((m) => m.id !== id);
    persist();
  },

  // ---- Activity --------------------------------------------------
  listActivity: () => state.activity,
  logExport: (module: string) => {
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: `Export performed — ${module}`, result: 'info' });
    persist();
  },
  logDiagnosticsRun: (issueCount: number) => {
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: `Diagnostics run (preview) — ${issueCount} issue(s) found`, result: issueCount > 0 ? 'warn' : 'good' });
    persist();
  },
  logLocalCommand: (query: string, matched: boolean) => {
    pushActivity({ actorType: 'user', actorLabel: LOCAL_ACTOR, action: `Local command executed — "${query}"`, result: matched ? 'good' : 'info' });
    persist();
  },

  // ---- Notifications (derived, not a separate stored list of content) --
  listNotifications: (): AppNotification[] => {
    const notes: AppNotification[] = [];
    if (state.settings.notificationPrefs.followupDue) {
      state.followups.filter((f) => f.status === 'open' && f.dueDate <= '2026-08-19').forEach((f) =>
        notes.push({ id: `n-fu-${f.id}`, title: `Follow-up due: ${f.entityLabel}`, detail: f.title, priority: f.priority === 'high' ? 'crit' : 'warn', entityRoute: f.entityType === 'creator' ? `/creators/${f.entityId}` : `/partnerships/${f.entityId}`, read: false, createdAt: f.createdAt })
      );
    }
    if (state.settings.notificationPrefs.approvalWaiting) {
      state.approvals.filter((a) => a.status === 'pending').forEach((a) =>
        notes.push({ id: `n-appr-${a.id}`, title: `Approval waiting: ${a.actionTitle}`, priority: 'warn', entityRoute: '/approvals', read: false, createdAt: a.createdAt })
      );
    }
    if (state.settings.notificationPrefs.creatorAtRisk) {
      state.creators.filter((c) => !c.archived && c.riskLevel === 'high').forEach((c) =>
        notes.push({ id: `n-risk-${c.id}`, title: `Creator at risk: ${c.fullName}`, priority: 'crit', entityRoute: `/creators/${c.id}`, read: false, createdAt: c.timeline[0]?.createdAt ?? c.joinDate })
      );
    }
    if (state.settings.notificationPrefs.contractExpiring) {
      state.contracts.filter((c) => !c.archived && c.status === 'expiring_soon').forEach((c) =>
        notes.push({ id: `n-con-${c.id}`, title: `Contract expiring: ${c.title}`, priority: 'warn', entityRoute: '/contracts', read: false, createdAt: c.effectiveDate ?? '2026-01-01' })
      );
    }
    return notes
      .filter((n) => !state.clearedNotificationIds.includes(n.id))
      .map((n) => ({ ...n, read: state.notificationsReadIds.includes(n.id) }))
      .sort((a, b) => (a.read === b.read ? 0 : a.read ? 1 : -1));
  },
  markNotificationRead: (id: string, read = true) => {
    state.notificationsReadIds = state.notificationsReadIds.filter((x) => x !== id);
    if (read) state.notificationsReadIds.push(id);
    persist();
  },
  markAllNotificationsRead: () => {
    const ids = demoDataService.listNotifications().map((n) => n.id);
    state.notificationsReadIds = Array.from(new Set([...state.notificationsReadIds, ...ids]));
    persist();
  },
  clearNotification: (id: string) => {
    state.clearedNotificationIds.push(id);
    persist();
  },

  // ---- Settings --------------------------------------------------
  getSettings: (): DemoSettings => state.settings,
  updateSettings: (patch: Partial<DemoSettings>) => {
    state.settings = { ...state.settings, ...patch };
    persist();
  },

  // ---- Connector local overrides (label/enabled-UI/notes only — never a fake connection) --
  getConnectorOverride: (key: string): ConnectorOverride => state.connectorOverrides[key] ?? {},
  setConnectorOverride: (key: string, patch: ConnectorOverride) => {
    state.connectorOverrides[key] = { ...state.connectorOverrides[key], ...patch };
    persist();
  },

  // ---- Reset / Clear --------------------------------------------------
  resetDemoData: () => {
    state = seed();
    persist();
  },
  clearAllDemoData: () => {
    state = {
      ...seed(),
      creators: [], approvals: [], partnerships: [], contracts: [], followups: [], activity: [],
      automations: [], memoryEntries: [],
      agents: seed().agents, // agent catalog is config-derived, not "data" to clear
    };
    persist();
  },
};
